import {
  marketplaceCardReviewKey,
  marketplaceExceptionReviewKey,
  type CdpImportDetail,
  type EbayImportDetail,
  type ExternalListingChecklistOption,
  type MarketplaceReconciliationDecision,
  type MarketplaceReconciliationSubject,
  type SuggestExternalListingChecklistResponse,
} from '@tcvs/shared';

export type WorkbenchFilter =
  | 'all'
  | 'matched'
  | 'exceptions'
  | 'cdp-only'
  | 'ebay-only'
  | 'conflicts';

export type WorkbenchStatus = Exclude<WorkbenchFilter, 'all' | 'exceptions'>;

/** Filters operator decisions without changing the immutable imported comparison. */
export type ReconciliationDecisionFilter =
  | 'open'
  | 'all'
  | 'unreviewed'
  | 'reconciled'
  | 'externally-managed'
  | 'not-active-on-ebay'
  | 'not-in-tcvs-expected'
  | 'needs-investigation'
  | 'ignored';

export interface ReconciliationWorkbenchRow {
  status: WorkbenchStatus;
  subject: MarketplaceReconciliationSubject;
  reviewKey: string;
  cardId: string;
  cdpRows: readonly CdpImportDetail['rows'][number][];
  ebayRows: readonly EbayImportDetail['rows'][number][];
}

export interface ReconciliationExceptionRow {
  source: 'CDP' | 'eBay';
  subject: 'exception' | 'external-listing';
  reviewKey: string;
  rowNumber: number;
  externalId: string;
  listingUrl?: string;
  title?: string;
  reason: string;
  candidateCardIds?: readonly string[];
}

/** Builds a public eBay item link only from a plausible numeric listing identifier. */
export function ebayListingUrl(itemNumber: string): string | undefined {
  const normalized = itemNumber.trim();
  return /^\d{9,15}$/.test(normalized) ? `https://www.ebay.com/itm/${normalized}` : undefined;
}

export interface ReconciliationWorkbench {
  rows: readonly ReconciliationWorkbenchRow[];
  exceptions: readonly ReconciliationExceptionRow[];
  linkedExternalCount: number;
  counts: Record<WorkbenchFilter, number>;
}

export interface ExternalListingSuggestionPrefill {
  checklistId?: string;
  cardNumber?: string;
  options: ExternalListingChecklistOption[];
  optionId: string;
  message: string;
}

/** Maps an advisory server suggestion into editable form state without confirming the match. */
export function externalListingSuggestionPrefill(
  suggestion: SuggestExternalListingChecklistResponse,
): ExternalListingSuggestionPrefill {
  const heading =
    suggestion.tier === 'HIGH_CONFIDENCE'
      ? 'High confidence'
      : suggestion.tier === 'REVIEWABLE'
        ? 'Review suggested match'
        : suggestion.tier === 'AMBIGUOUS'
          ? 'Multiple matches'
          : undefined;
  return {
    ...(suggestion.checklistId === undefined ? {} : { checklistId: suggestion.checklistId }),
    ...(suggestion.cardNumber === undefined ? {} : { cardNumber: suggestion.cardNumber }),
    options: suggestion.options,
    optionId: suggestion.recommendedOptionId ?? '',
    message: heading === undefined ? suggestion.reason : `${heading}: ${suggestion.reason}`,
  };
}

/**
 * Joins only uniquely matched cards. Title, price, and legacy SKU text are never used to infer a
 * match: the persisted reconciliation result is the sole source of truth.
 */
export function buildReconciliationWorkbench(
  cdp: CdpImportDetail,
  ebay: EbayImportDetail,
): ReconciliationWorkbench {
  const cdpByCard = matchedRowsByCard(cdp.rows);
  const ebayByCard = matchedRowsByCard(ebay.rows);
  const cardIds = new Set([...cdpByCard.keys(), ...ebayByCard.keys()]);
  const rows = [...cardIds].sort().map((cardId) => {
    const cdpRows = cdpByCard.get(cardId) ?? [];
    const ebayRows = ebayByCard.get(cardId) ?? [];
    const status: WorkbenchStatus =
      cdpRows.length > 1 || ebayRows.length > 1
        ? 'conflicts'
        : cdpRows.length === 0
          ? 'ebay-only'
          : ebayRows.length === 0
            ? 'cdp-only'
            : 'matched';
    return {
      status,
      subject: status,
      reviewKey: marketplaceCardReviewKey(cardId),
      cardId,
      cdpRows,
      ebayRows,
    };
  });
  const exceptions = [
    ...cdp.rows
      .filter((row) => row.reviewStatus === 'NEEDS_REVIEW')
      .map((row) => ({
        source: 'CDP' as const,
        subject: 'exception' as const,
        reviewKey: marketplaceExceptionReviewKey('CDP', row.rowNumber),
        rowNumber: row.rowNumber,
        externalId: row.cdpSku,
        title: row.title,
        reason: row.matchMethod,
        candidateCardIds: row.candidateCardIds,
      })),
    ...ebay.rows
      .filter((row) => row.reviewStatus === 'NEEDS_REVIEW' && row.checklistLink === undefined)
      .map((row) => ({
        source: 'eBay' as const,
        subject: 'external-listing' as const,
        reviewKey: marketplaceExceptionReviewKey('EBAY', row.rowNumber),
        rowNumber: row.rowNumber,
        externalId: row.itemNumber || row.customLabel || '-',
        ...(ebayListingUrl(row.itemNumber) === undefined
          ? {}
          : { listingUrl: ebayListingUrl(row.itemNumber) }),
        title: row.title,
        reason: row.matchMethod,
        candidateCardIds: row.candidateCardIds,
      })),
  ];
  const counts: Record<WorkbenchFilter, number> = {
    all: rows.length,
    matched: 0,
    exceptions: exceptions.length,
    'cdp-only': 0,
    'ebay-only': 0,
    conflicts: 0,
  };
  for (const row of rows) counts[row.status] += 1;
  return {
    rows,
    exceptions,
    linkedExternalCount: ebay.rows.filter((row) => row.checklistLink !== undefined).length,
    counts,
  };
}

export function filterWorkbenchRows(
  workbench: ReconciliationWorkbench,
  filter: WorkbenchFilter,
): readonly ReconciliationWorkbenchRow[] {
  return filter === 'all' ? workbench.rows : workbench.rows.filter((row) => row.status === filter);
}

/** Open work is either unreviewed or explicitly returned for investigation. */
export function matchesReconciliationDecisionFilter(
  decision: MarketplaceReconciliationDecision | undefined,
  filter: ReconciliationDecisionFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'open':
      return decision === undefined || decision === 'NEEDS_INVESTIGATION';
    case 'unreviewed':
      return decision === undefined;
    case 'reconciled':
      return decision === 'RECONCILED';
    case 'externally-managed':
      return decision === 'CONFIRMED_EXTERNALLY_MANAGED';
    case 'not-active-on-ebay':
      return decision === 'NOT_ACTIVE_ON_EBAY';
    case 'not-in-tcvs-expected':
      return decision === 'NOT_IN_TCVS_EXPECTED';
    case 'needs-investigation':
      return decision === 'NEEDS_INVESTIGATION';
    case 'ignored':
      return decision === 'IGNORED';
  }
}

export function exceptionsCsv(exceptions: readonly ReconciliationExceptionRow[]): string {
  const header = ['source', 'row_number', 'external_id', 'title', 'reason', 'candidate_card_ids'];
  const rows = exceptions.map((row) => [
    row.source,
    String(row.rowNumber),
    row.externalId,
    row.title ?? '',
    row.reason,
    row.candidateCardIds?.join('|') ?? '',
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}

function matchedRowsByCard<TRow extends { reviewStatus: string; card?: { cardId: string } }>(
  rows: readonly TRow[],
): Map<string, TRow[]> {
  const byCard = new Map<string, TRow[]>();
  for (const row of rows) {
    if (row.reviewStatus !== 'MATCHED' || row.card === undefined) continue;
    const existing = byCard.get(row.card.cardId) ?? [];
    existing.push(row);
    byCard.set(row.card.cardId, existing);
  }
  return byCard;
}

function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}
